import { DatePipe } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { BaseComponent } from 'src/app/core/components/base/base.component';
import { ToasterTypes } from 'src/app/core/enum/toaster-type.enum';
import { Note } from 'src/app/data/model/note';
import { NotesService } from 'src/app/data/service/notes.service';
import { OrganizationService } from 'src/app/data/service/organization.service';
import { SuppliersService } from 'src/app/data/service/suppliers.service';
import { UserService } from 'src/app/data/service/user.service';
import { ToasterMessageService } from 'src/app/shared/toaster-message/toaster-message.service';
import { take } from 'rxjs/operators';
import { InvoiceActivity } from '../../models/activity/activity';
import { NewSupplier } from '../../models/supplier/new-supplier';

/** A teammate that can be @mentioned. */
interface Member {
  id: string;
  name: string;
  emailId: string;
}

/** One lifecycle event, mapped to an icon + tone for the timeline. */
interface TimelineItem {
  id: string;
  title: string;
  detail: string;
  actor: string;
  ts: Date;
  icon: string;
  tone: string;
}

interface DayGroup {
  label: string;
  items: TimelineItem[];
}

/** A run of plain text or an @mention token, used to render note bodies safely. */
interface NotePart {
  text: string;
  mention: boolean;
}

/** A note rendered in the conversation thread. */
interface NoteVM {
  id: string;
  author: string;
  mine: boolean;
  ts: Date;
  parts: NotePart[];
  /** Visual-only category (Billing / AP / Coding / Approval). Session-scoped. */
  category: string;
}

/** A selectable note category. Presentational only until the API gains a field. */
interface NoteType {
  id: string;
  label: string;
  tone: string;
}

interface MentionMenu {
  start: number;
  caret: number;
  items: Member[];
  active: number;
}

/**
 * Activity tab — Direction C.
 *
 * A segmented switcher between a day-grouped lifecycle Activity timeline and a
 * focused Notes conversation. The Activity timeline reads the real supplier
 * activity API; the Notes thread reads/writes the real Notes API — text,
 * @mentions and the note's category all persist, and a note's author can edit
 * or delete their own.
 *
 * Reused beyond the contact panel: the Inbox invoice side-panel and the Bills-
 * to-Pay payment side-panel feed it a preloaded `activities` list plus a
 * `recordId`/`noteType`, so the same timeline + notes conversation renders for
 * invoices and payments without re-fetching the lifecycle.
 */
@Component({
  selector: 'app-contact-activity',
  templateUrl: './contact-activity.component.html',
  styleUrls: ['./contact-activity.component.scss'],
})
export class ContactActivityComponent
  extends BaseComponent
  implements OnInit, OnChanges
{
  @Input() supplier: NewSupplier;
  /** Which pane to show first. The tab opens on the lifecycle timeline. */
  @Input() defaultView: 'activity' | 'notes' = 'activity';

  /**
   * Record the notes belong to. Defaults to the supplier id (contact panel);
   * the invoice / payment side-panels pass the invoice or payment id instead.
   */
  @Input() recordId = '';
  /** Note `type` sent on create + used for the record context ('Contact' | 'Invoice' | 'Payment'). */
  @Input() noteType = 'Contact';
  /**
   * Preloaded lifecycle to render. When set, the timeline is built from this
   * list instead of fetching the supplier activity. The host owns the fetch.
   */
  @Input() activities: InvoiceActivity[] = null;
  /** Height of the panel — hosts size it to their own side-panel chrome. */
  @Input() panelHeight = 'calc(100vh - 131px)';

  view: 'activity' | 'notes' = 'activity';
  isSpinnerShown = false;

  // ---- Activity ----
  eventCount = 0;
  dayGroups: DayGroup[] = [];

  // ---- Notes ----
  notes: NoteVM[] = [];
  members: Member[] = [];
  typeFilter = 'all';
  filterOpen = false;

  readonly noteTypes: NoteType[] = [
    { id: 'general', label: 'General', tone: 'slate' },
    { id: 'billing', label: 'Billing', tone: 'blue' },
    { id: 'ap', label: 'Accounts Payable', tone: 'violet' },
  ];

  // ---- Composer ----
  composerText = '';
  composerType: NoteType = this.noteTypes[0];
  typeOpen = false;
  mentionMenu: MentionMenu | null = null;
  isSending = false;

  // ---- Editing ----
  /** The note being rewritten, and the note waiting on a delete confirmation. */
  editingId: string = null;
  editText = '';
  confirmDeleteId: string = null;
  isSavingEdit = false;

  /** Solid tone colours used for category dots. */
  readonly toneColor: { [key: string]: string } = {
    slate: '#8A8A87',
    blue: '#2563EB',
    violet: '#7C3AED',
    amber: '#B3540A',
    green: '#2F7D32',
  };

  @ViewChild('composerTa') composerTextarea: ElementRef<HTMLTextAreaElement>;
  @ViewChild('highlight') highlightEl: ElementRef<HTMLDivElement>;
  @ViewChild('filterWrap') filterWrap: ElementRef<HTMLElement>;
  @ViewChild('typeWrap') typeWrap: ElementRef<HTMLElement>;
  @ViewChild('composerRoot') composerRoot: ElementRef<HTMLElement>;

  private organizationId: string = null;
  private currentUserId: string = sessionStorage.Id;
  private currentUserName: string = sessionStorage.UserName;
  private datePipe: DatePipe = new DatePipe(this.getNavigatorLanguage());

  constructor(
    private readonly _suppliersService: SuppliersService,
    private readonly _notesService: NotesService,
    private readonly _userService: UserService,
    private readonly _organizationService: OrganizationService,
    private readonly _toasterMessageService: ToasterMessageService,
    private readonly router: Router
  ) {
    super();
  }

  ngOnInit(): void {
    this.view = this.defaultView;
    this.organizationId = this._organizationService.selectedOrganization;
    // A host that supplies its own lifecycle (invoice / payment panels) passes
    // `activities`; otherwise we fetch the supplier's lifecycle (contact panel).
    if (this.activities) {
      this.buildTimeline(this.activities);
    } else {
      this.loadActivity();
    }
    this.loadMembers();
    this.loadNotes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The host's activity fetch usually resolves after this component is
    // created, so rebuild the timeline whenever the input list updates.
    if (changes['activities'] && !changes['activities'].firstChange && this.activities) {
      this.buildTimeline(this.activities);
    }
  }

  /** Record id the notes hang off — supplier id by default, else the host's. */
  private get effectiveRecordId(): string {
    return this.recordId || this.supplier?.supplierId || '';
  }

  // ======================================================================
  // Data loading
  // ======================================================================

  private loadActivity(): void {
    if (!this.supplier?.supplierId) {
      return;
    }
    this.isSpinnerShown = true;
    this._suppliersService
      .getSupplierActivityById(this.organizationId, this.supplier.supplierId)
      .pipe(this.untilThis)
      .subscribe(
        (data: InvoiceActivity[]) => {
          this.buildTimeline(data || []);
          this.isSpinnerShown = false;
        },
        (e: Error) => {
          this.isSpinnerShown = false;
          this._toasterMessageService.displayToasterState(
            ToasterTypes.Error,
            'Error',
            e.message
          );
        }
      );
  }

  /** Map → sort (newest first) → day-group a lifecycle list into the timeline. */
  private buildTimeline(data: InvoiceActivity[]): void {
    const items = (data || [])
      .filter((a) => a.date != null)
      .map((a, i) => this.toTimelineItem(a, i))
      .sort((a, b) => b.ts.getTime() - a.ts.getTime());
    this.eventCount = items.length;
    this.dayGroups = this.groupByDay(items);
  }

  private loadMembers(): void {
    this._userService
      .getUsersByOrganizationId(this.organizationId)
      .pipe(take(1))
      .subscribe((res: any) => {
        this.members = (res?.data || []).map((u: any) => ({
          id: u['id'],
          name: u['name'],
          emailId: u['email'],
        }));
      });
  }

  private loadNotes(): void {
    const recordId = this.effectiveRecordId;
    if (!recordId) {
      return;
    }
    this._notesService
      .getNotesByOrganizationId(recordId, this.organizationId)
      .pipe(take(1))
      .subscribe(
        (notes: any[]) => {
          this.notes = (notes || []).map((n) => this.toNoteVM(n));
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(
            ToasterTypes.Error,
            'Error',
            e.message
          );
        }
      );
  }

  // ======================================================================
  // Mapping helpers
  // ======================================================================

  private toTimelineItem(a: InvoiceActivity, i: number): TimelineItem {
    const { icon, tone } = this.classifyStage(a.stage);
    const detail = a.note || (a as any).submissionMethod || a.model || '';
    return {
      id: 'e' + i,
      title: a.stage,
      detail,
      actor: a.actor,
      // UTC from the server, like a note's createdAt (and the legacy Activity list's
      // getFormattedDate): read as local, every stage showed the time hours off.
      ts: this.toDate(a.date, true),
      icon,
      tone,
    };
  }

  /** Map a lifecycle stage label to a timeline icon + tone. */
  private classifyStage(stage: string): { icon: string; tone: string } {
    const s = (stage || '').toLowerCase();
    if (s.includes('paid') || s.includes('payment')) {
      return { icon: 'card', tone: 'green' };
    }
    if (s.includes('approve')) {
      return { icon: 'check', tone: 'green' };
    }
    if (s.includes('publish') || s.includes('sync') || s.includes('xero')) {
      return { icon: 'xero', tone: 'xero' };
    }
    if (s.includes('extract') || s.includes('recogn') || s.includes('read')) {
      return { icon: 'sparkle', tone: 'purple' };
    }
    if (s.includes('cod') || s.includes('categor') || s.includes('tax')) {
      return { icon: 'tag', tone: 'amber' };
    }
    if (s.includes('edit') || s.includes('updat') || s.includes('chang')) {
      return { icon: 'edit', tone: 'amber' };
    }
    if (
      s.includes('submit') ||
      s.includes('upload') ||
      s.includes('sent') ||
      s.includes('receiv') ||
      s.includes('creat') ||
      s.includes('document')
    ) {
      return { icon: 'cloud', tone: 'blue' };
    }
    return { icon: 'dot', tone: 'slate' };
  }

  private groupByDay(items: TimelineItem[]): DayGroup[] {
    const groups: DayGroup[] = [];
    items.forEach((it) => {
      const label = this.dayHeading(it.ts);
      let g = groups.find((x) => x.label === label);
      if (!g) {
        g = { label, items: [] };
        groups.push(g);
      }
      g.items.push(it);
    });
    return groups;
  }

  private toNoteVM(n: any): NoteVM {
    return {
      id: n.id || n.noteId || 'n' + Math.round(this.timeOf(n.createdAt)),
      author: n.name || n.createdBy || 'Unknown',
      mine: n.userID === this.currentUserId,
      ts: this.toDate(n.createdAt, true),
      parts: this.renderParts(n.details || ''),
      category: n.category || 'general',
    };
  }

  private timeOf(raw: any): number {
    const d = this.toDate(raw, true);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  }

  /**
   * Split a stored note body into plain-text and @mention runs. Mentions are
   * stored wrapped in <b>…</b>; we strip those tags and re-detect mentions
   * against the known member list so they render as styled tokens (and nothing
   * is injected as HTML — every run is interpolated as text).
   */
  private renderParts(details: string): NotePart[] {
    const clean = (details || '').replace(/<\/?b>/gi, '');
    const re = this.mentionRegex();
    if (!re) {
      return [{ text: clean, mention: false }];
    }
    const parts: NotePart[] = [];
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(clean)) !== null) {
      if (m.index > last) {
        parts.push({ text: clean.slice(last, m.index), mention: false });
      }
      parts.push({ text: '@' + m[1], mention: true });
      last = m.index + m[0].length;
    }
    if (last < clean.length) {
      parts.push({ text: clean.slice(last), mention: false });
    }
    return parts.length ? parts : [{ text: clean, mention: false }];
  }

  private mentionRegex(): RegExp | null {
    if (!this.members.length) {
      return null;
    }
    const names = this.members
      .map((mb) => mb.name)
      .filter(Boolean)
      .sort((a, b) => b.length - a.length)
      .map((n) => this.escapeRegex(n));
    if (!names.length) {
      return null;
    }
    return new RegExp('@(' + names.join('|') + ')', 'g');
  }

  private escapeRegex(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // ======================================================================
  // View getters
  // ======================================================================

  /** Categories actually present in the thread (drives the filter bar). */
  get presentTypes(): NoteType[] {
    return this.noteTypes.filter((t) =>
      this.notes.some((n) => (n.category || 'general') === t.id)
    );
  }

  get shownNotes(): NoteVM[] {
    if (this.typeFilter === 'all') {
      return this.notes;
    }
    return this.notes.filter((n) => (n.category || 'general') === this.typeFilter);
  }

  get activeType(): NoteType | null {
    return this.typeFilter === 'all'
      ? null
      : this.noteTypes.find((t) => t.id === this.typeFilter) || null;
  }

  countFor(id: string): number {
    return this.notes.filter((n) => (n.category || 'general') === id).length;
  }

  typeLabel(id: string): string {
    return this.noteTypes.find((t) => t.id === id)?.label || '';
  }

  typeTone(id: string): string {
    return this.noteTypes.find((t) => t.id === id)?.tone || 'slate';
  }

  /** Members currently @mentioned in the composer — the "Notifying" row. */
  get notifying(): Member[] {
    return this.extractMentions(this.composerText);
  }

  // ======================================================================
  // Interactions — view + filter
  // ======================================================================

  setView(v: 'activity' | 'notes'): void {
    this.view = v;
  }

  setTypeFilter(id: string): void {
    this.typeFilter = id;
    this.filterOpen = false;
  }

  toggleFilter(): void {
    this.filterOpen = !this.filterOpen;
  }

  selectComposerType(t: NoteType): void {
    this.composerType = t;
    this.typeOpen = false;
  }

  toggleTypeMenu(): void {
    this.typeOpen = !this.typeOpen;
  }

  // ======================================================================
  // Interactions — composer
  // ======================================================================

  onComposerInput(e: Event): void {
    const ta = e.target as HTMLTextAreaElement;
    // The textarea owns its content while typing (uncontrolled) — we only read
    // from it here so live typing is never reset/lagged by change detection.
    this.composerText = ta.value;
    this.mentionMenu = this.computeMenu(ta.value, ta.selectionStart);
    this.grow();
  }

  /** Set the textarea value imperatively (for programmatic edits only). */
  private setComposerText(next: string): void {
    this.composerText = next;
    const ta = this.composerTextarea?.nativeElement;
    if (ta) {
      ta.value = next;
    }
  }

  onComposerKeydown(e: KeyboardEvent): void {
    if (this.mentionMenu) {
      const menu = this.mentionMenu;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        menu.active = (menu.active + 1) % menu.items.length;
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        menu.active = (menu.active - 1 + menu.items.length) % menu.items.length;
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        this.pickMention(menu.items[menu.active]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        this.mentionMenu = null;
        return;
      }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      this.send();
    }
  }

  syncScroll(): void {
    if (this.highlightEl && this.composerTextarea) {
      this.highlightEl.nativeElement.scrollTop =
        this.composerTextarea.nativeElement.scrollTop;
    }
  }

  /** Render the live-highlight layer behind the textarea (mentions in blue). */
  get highlightParts(): NotePart[] {
    const re = this.mentionRegex();
    if (!re) {
      return [{ text: this.composerText + '\n', mention: false }];
    }
    const parts: NotePart[] = [];
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(this.composerText)) !== null) {
      if (m.index > last) {
        parts.push({ text: this.composerText.slice(last, m.index), mention: false });
      }
      parts.push({ text: '@' + m[1], mention: true });
      last = m.index + m[0].length;
    }
    if (last < this.composerText.length) {
      parts.push({ text: this.composerText.slice(last), mention: false });
    }
    // trailing guard so a final newline still renders a line
    parts.push({ text: '\n', mention: false });
    return parts;
  }

  insertAt(): void {
    const ta = this.composerTextarea?.nativeElement;
    const pos = ta ? ta.selectionStart : this.composerText.length;
    const needSpace = pos > 0 && !/\s/.test(this.composerText[pos - 1]);
    const ins = needSpace ? ' @' : '@';
    const next =
      this.composerText.slice(0, pos) + ins + this.composerText.slice(pos);
    const caret = pos + ins.length;
    this.setComposerText(next);
    requestAnimationFrame(() => {
      if (ta) {
        ta.focus();
        ta.setSelectionRange(caret, caret);
      }
      this.mentionMenu = this.computeMenu(next, caret);
      this.grow();
    });
  }

  dropMention(name: string): void {
    this.setComposerText(this.removeMention(this.composerText, name));
    requestAnimationFrame(() => {
      this.composerTextarea?.nativeElement.focus();
      this.grow();
    });
  }

  pickMention(m: Member): void {
    if (!this.mentionMenu) {
      return;
    }
    const before = this.composerText.slice(0, this.mentionMenu.start);
    const after = this.composerText.slice(this.mentionMenu.caret);
    const insert = '@' + m.name + ' ';
    const next = before + insert + after;
    this.setComposerText(next);
    this.mentionMenu = null;
    requestAnimationFrame(() => {
      const ta = this.composerTextarea?.nativeElement;
      if (ta) {
        const pos = (before + insert).length;
        ta.focus();
        ta.setSelectionRange(pos, pos);
      }
      this.grow();
    });
  }

  send(): void {
    const text = this.composerText.trim();
    if (!text || this.isSending) {
      return;
    }
    const mentions = this.extractMentions(text);
    const category = this.composerType;
    const details = this.toDetails(text);

    const viewNoteURL =
      this.router.url +
      '?organizationId=' +
      this.organizationId +
      '&recordId=' +
      this.effectiveRecordId;

    const note: Note = {
      details,
      type: this.noteType,
      organizationID: this.organizationId,
      userID: this.currentUserId,
      recordID: this.effectiveRecordId,
      noteMentions: mentions,
      createdBy: this.currentUserName,
      viewNoteURL,
      createdAt: null,
      category: category.id,
    };

    this.isSending = true;
    this._notesService
      .createNote(note)
      .pipe(take(1))
      .subscribe(
        () => {
          // Appended rather than refetched, so the thread doesn't flicker. The
          // id is local until the next load, which is why it cannot be edited
          // straight away — see `canModify`.
          this.notes = [
            ...this.notes,
            {
              id: 'local-' + this.notes.length + '-' + text.length,
              author: this.currentUserName || 'You',
              mine: true,
              ts: new Date(),
              parts: this.renderParts(text),
              category: category.id,
            },
          ];
          this.setComposerText('');
          this.composerType = this.noteTypes[0];
          this.mentionMenu = null;
          this.isSending = false;
          // Lists showing note counts for this record are now out of date.
          this._notesService.notifyChanged(this.effectiveRecordId);
          requestAnimationFrame(() => this.grow());
        },
        (e: Error) => {
          this.isSending = false;
          this._toasterMessageService.displayToasterState(
            ToasterTypes.Error,
            'Error',
            e.message
          );
        }
      );
  }

  // ======================================================================
  // Editing
  // ======================================================================

  /** Mentions are stored wrapped in <b>…</b> — the shape `send` writes. */
  private toDetails(text: string): string {
    let details = text;
    this.extractMentions(text).forEach((mb) => {
      const token = '@' + mb.name;
      details = details.split(token).join('<b>' + token + '</b>');
    });
    return details;
  }

  /** The note body as it was typed, rebuilt from its rendered runs. */
  private plainTextOf(note: NoteVM): string {
    return (note.parts || []).map((p) => p.text).join('');
  }

  /**
   * Own notes only, and not one appended locally after sending — that copy has
   * no server id to update against until the thread is next loaded.
   */
  canModify(note: NoteVM): boolean {
    return !!note && note.mine && !(note.id || '').startsWith('local-');
  }

  startEdit(note: NoteVM): void {
    this.confirmDeleteId = null;
    this.editingId = note.id;
    this.editText = this.plainTextOf(note);
  }

  cancelEdit(): void {
    this.editingId = null;
    this.editText = '';
  }

  saveEdit(note: NoteVM): void {
    const text = (this.editText || '').trim();
    if (!text || this.isSavingEdit) {
      return;
    }

    if (text === this.plainTextOf(note)) {
      this.cancelEdit();
      return;
    }

    this.isSavingEdit = true;
    this._notesService
      .updateNote(note.id, {
        details: this.toDetails(text),
        organizationID: this.organizationId,
        recordID: this.effectiveRecordId,
        userID: this.currentUserId,
        noteMentions: this.extractMentions(text),
      })
      .pipe(take(1))
      .subscribe(
        () => {
          note.parts = this.renderParts(text);
          this.isSavingEdit = false;
          this.cancelEdit();
          this._notesService.notifyChanged(this.effectiveRecordId);
        },
        (e: Error) => {
          this.isSavingEdit = false;
          this._toasterMessageService.displayToasterState(
            ToasterTypes.Error,
            'Error',
            e.message
          );
        }
      );
  }

  askDelete(note: NoteVM): void {
    this.cancelEdit();
    this.confirmDeleteId = note.id;
  }

  cancelDelete(): void {
    this.confirmDeleteId = null;
  }

  confirmDelete(note: NoteVM): void {
    this._notesService
      .deleteNote(note.id, this.organizationId)
      .pipe(take(1))
      .subscribe(
        () => {
          this.notes = this.notes.filter((n) => n.id !== note.id);
          this.confirmDeleteId = null;
          this._notesService.notifyChanged(this.effectiveRecordId);
        },
        (e: Error) => {
          this.confirmDeleteId = null;
          this._toasterMessageService.displayToasterState(
            ToasterTypes.Error,
            'Error',
            e.message
          );
        }
      );
  }

  // ======================================================================
  // Mention text utilities
  // ======================================================================

  private computeMenu(val: string, caret: number): MentionMenu | null {
    const upto = val.slice(0, caret);
    const at = upto.lastIndexOf('@');
    if (at < 0) {
      return null;
    }
    const before = at === 0 ? '' : upto[at - 1];
    if (before && !/\s/.test(before)) {
      return null;
    }
    const q = upto.slice(at + 1);
    if (/\n/.test(q) || q.length > 20) {
      return null;
    }
    const items = this.members.filter((m) =>
      m.name.toLowerCase().includes(q.trim().toLowerCase())
    );
    if (!items.length) {
      return null;
    }
    return { start: at, caret, items, active: 0 };
  }

  private extractMentions(text: string): Member[] {
    const re = this.mentionRegex();
    if (!re) {
      return [];
    }
    const out: Member[] = [];
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const found = this.members.find((mb) => mb.name === m[1]);
      if (found && !out.some((o) => o.name === found.name)) {
        out.push(found);
      }
    }
    return out;
  }

  private removeMention(text: string, name: string): string {
    return text
      .replace(new RegExp('@' + this.escapeRegex(name) + '\\s?'), '')
      .replace(/\s{2,}/g, ' ');
  }

  private grow(): void {
    const ta = this.composerTextarea?.nativeElement;
    if (!ta) {
      return;
    }
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 132) + 'px';
    this.syncScroll();
  }

  // ======================================================================
  // Outside-click handling for popovers
  // ======================================================================

  @HostListener('document:mousedown', ['$event'])
  onDocMouseDown(ev: MouseEvent): void {
    const target = ev.target as Node;
    if (
      this.filterOpen &&
      this.filterWrap &&
      !this.filterWrap.nativeElement.contains(target)
    ) {
      this.filterOpen = false;
    }
    if (
      this.typeOpen &&
      this.typeWrap &&
      !this.typeWrap.nativeElement.contains(target)
    ) {
      this.typeOpen = false;
    }
    if (
      this.mentionMenu &&
      this.composerRoot &&
      !this.composerRoot.nativeElement.contains(target)
    ) {
      this.mentionMenu = null;
    }
  }

  // ======================================================================
  // Avatars + date formatting
  // ======================================================================

  initials(name: string): string {
    if (!name) {
      return 'U';
    }
    return name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }

  /** Stable 1..6 avatar colour class from a name (matches app palette). */
  avatarClass(name: string): string {
    let h = 0;
    const s = name || '';
    for (let i = 0; i < s.length; i++) {
      h = (h * 31 + s.charCodeAt(i)) % 6;
    }
    return 'sup_img_box_' + (h + 1);
  }

  stamp(d: Date): string {
    return this.dayLabel(d) + ' at ' + this.timeLabel(d);
  }

  /** A timeline row's time; its day is the group heading. In the viewer's timezone, like everything here. */
  clock(d: Date): string {
    return this.timeLabel(d);
  }

  /** On hover: the whole date and time, with the zone it is shown in. */
  fullStamp(d: Date): string {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    return (this.datePipe.transform(d, 'EEEE d MMMM y, h:mm a') || '') + (zone ? ` (${zone})` : '');
  }

  /** A timeline day heading: Today, Yesterday, or "Tue 6 Oct" (with the year when it isn't this one). */
  private dayHeading(d: Date): string {
    const label = this.dayLabel(d);
    if (label === 'Today' || label === 'Yesterday') {
      return label;
    }
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return this.datePipe.transform(d, sameYear ? 'EEE d MMM' : 'EEE d MMM y') || label;
  }

  private timeLabel(d: Date): string {
    return this.datePipe.transform(d, 'h:mm a') || '';
  }

  private dayLabel(d: Date): string {
    const now = new Date();
    if (this.sameDay(d, now)) {
      return 'Today';
    }
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    if (this.sameDay(d, y)) {
      return 'Yesterday';
    }
    return this.datePipe.transform(d, 'd MMM') || '';
  }

  private sameDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  private toDate(raw: any, isUtc: boolean): Date {
    if (raw instanceof Date) {
      return raw;
    }
    if (typeof raw === 'string') {
      // The server's UTC times carry no zone; one that already does (Z or ±hh:mm) is taken as given.
      // A bare date has no time to convert.
      const zoned = /(Z|[+-]\d{2}:?\d{2})$/i.test(raw.trim());
      const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw.trim());
      return new Date(isUtc && !zoned && !dateOnly ? raw.trim().replace(' ', 'T') + 'Z' : raw);
    }
    return new Date(raw);
  }

  private getNavigatorLanguage(): string {
    if (navigator.languages && navigator.languages.length) {
      return navigator.languages[0];
    }
    return 'en-US';
  }

  trackByGroup(_i: number, g: DayGroup): string {
    return g.label;
  }

  trackByItem(_i: number, it: TimelineItem): string {
    return it.id;
  }

  trackByNote(_i: number, n: NoteVM): string {
    return n.id;
  }
}
